import React, { useEffect, useRef, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { apiService } from '../services/api';

type CacheEntry = { data: string | null; promise?: Promise<string | null> };

// Base64 payloads are large, so keep only the most recent entries.
const MAX_CACHE_ENTRIES = 150;
const imageCache = new Map<string, CacheEntry>();

const remember = (filename: string, entry: CacheEntry) => {
  imageCache.delete(filename);
  imageCache.set(filename, entry);
  if (imageCache.size > MAX_CACHE_ENTRIES) {
    const oldest = imageCache.keys().next().value;
    if (oldest !== undefined) imageCache.delete(oldest);
  }
};

export const clearImageCache = () => imageCache.clear();

export const placeholderImage =
  'data:image/svg+xml;charset=utf-8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"><rect width="200" height="200" fill="#f5f5f5"/></svg>'
  );

export const fetchImageData = (filename: string): Promise<string | null> => {
  const cached = imageCache.get(filename);
  if (cached?.data) return Promise.resolve(cached.data);
  if (cached?.promise) return cached.promise;

  const promise = (async () => {
    try {
      const token =
        localStorage.getItem('admin_token') ||
        apiService.getAccessToken() ||
        localStorage.getItem('access_token');
      if (!token) {
        imageCache.delete(filename);
        return null;
      }
      const response = await fetch(
        `https://pucollection.com/api/image-data/${filename}?token=${token}`
      );
      const data = await response.json();
      if (data.success) {
        remember(filename, { data: data.data_url });
        return data.data_url as string;
      }
      imageCache.delete(filename);
      return null;
    } catch {
      imageCache.delete(filename);
      return null;
    }
  })();

  imageCache.set(filename, { data: null, promise });
  return promise;
};

interface LazyImageProps {
  filename: string;
  alt: string;
  style?: React.CSSProperties;
  onClick?: () => void;
}

// Loads image data only once the element approaches the viewport.
const LazyImage: React.FC<LazyImageProps> = ({ filename, alt, style, onClick }) => {
  const cachedData = imageCache.get(filename)?.data || null;
  const [src, setSrc] = useState<string | null>(cachedData);
  const [failed, setFailed] = useState(false);
  const holderRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (src) return;
    const node = holderRef.current;
    let cancelled = false;
    const load = () => {
      fetchImageData(filename).then((data) => {
        if (cancelled) return;
        if (data) setSrc(data);
        else setFailed(true);
      });
    };
    if (!node || typeof IntersectionObserver === 'undefined') {
      load();
      return () => {
        cancelled = true;
      };
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          observer.disconnect();
          load();
        }
      },
      { rootMargin: '300px' }
    );
    observer.observe(node);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [filename, src]);

  if (!src) {
    return (
      <span
        ref={holderRef}
        role={failed ? 'img' : 'status'}
        aria-label={failed ? `${alt} (failed to load)` : `Loading ${alt}`}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', minHeight: 80 }}
      >
        {failed ? <span aria-hidden>⚠️</span> : <CircularProgress size={24} aria-hidden />}
      </span>
    );
  }

  const interactive = Boolean(onClick);
  return (
    <img
      src={src}
      alt={alt}
      decoding="async"
      draggable={false}
      style={{ ...style, cursor: interactive ? 'pointer' : undefined }}
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      translate="no"
      className="notranslate"
    />
  );
};

export default React.memo(
  LazyImage,
  (prev, next) => prev.filename === next.filename && prev.alt === next.alt
);
